<script>
  import {
    ConfirmModal,
    GroupModal,
    Layout,
    QuotaProgress,
    InfoBox,
  } from "../../components/index";
  import { ModalType } from "../../types/modal";
  import {
    faPlus,
    faTrashAlt,
    faPen,
    faClone,
  } from "@fortawesome/pro-regular-svg-icons";
  import {
    faSort,
    faSortDown,
    faSortUp,
  } from "@fortawesome/pro-solid-svg-icons";
  import {
    ContractsRepository,
    GroupsRepository,
    RolesRepository,
  } from "../../repositories/index";
  import {
    Button,
    Flex,
    FlexItem,
    Icon,
    Text,
    SimpleDataTable,
    DataCell,
    Select,
    TextField,
    SelectItem,
    DataTableExpansion,
    DataTableExpansionContent,
    ProgressBar,
  } from "@gl/design-system";
  import { onMount, getContext } from "svelte";
  import { _, contract as contractStore, callout } from "../../stores/index";
  import { Link, navigate } from "svelte-routing";
  import { parseUrl, setUrl, setModalParams, clearModalParams } from "../../utils/index";
  import { format } from "date-fns";
  import DuplicateGroupModal from "../../components/DuplicateModal.svelte";

  const { isAuthorized } = getContext("isAuthorized");

  const loading = {};

  let groups = [];
  let roles = [];
  let contract;

  let modal = false;
  let duplicateModal;
  let confirmModal;
  let modalType;
  let selectedGroup;

  let search = { name: "", role: "all" };

  let order = {
    asc: true,
  };
  let searched;

  async function fetchGroups() {
    try {
      loading.groups = true;
      const result = await GroupsRepository.getGroups();
      groups = result.list;
      searched = groups;
    } finally {
      loading.groups = false;
    }
  }

  async function fetchRoles() {
    try {
      loading.roles = true;
      const result = await RolesRepository.getRoles();
      roles = result.list;
    } finally {
      loading.roles = false;
    }
  }

  async function fetchContract() {
    loading.contract = true;
    try {
      contract = await ContractsRepository.getContract($contractStore.id);
    } finally {
      loading.contract = false;
    }
  }

  function sort(field = null, toggleAsc = true) {
    if (toggleAsc) {
      order.asc = !order.asc;
    }
    order.by = field;

    const cmpA = order.asc ? -1 : 1;
    const cmpB = order.asc ? 1 : -1;
    searched = searched.sort((a, b) => {
      const A =
        field === "modified"
          ? a.metadata?.modifiedAt
          : a[order.by].toUpperCase();
      const B =
        field === "modified"
          ? b.metadata?.modifiedAt
          : b[order.by].toUpperCase();
      return A === B ? 0 : A < B ? cmpA : cmpB;
    });
    order.icon = order.asc ? "ArrowDown" : "ArrowUp";
    setUrl({
      ...search,
      orderBy: order.by,
      orderAsc: order.asc,
      groupModal: modalType,
      groupId: selectedGroup?.id,
    });
  }

  async function onDeleteGroup(group) {
    try {
      await GroupsRepository.deleteGroup(group.id);
    } finally {
      groups = groups.filter((g) => g !== group);
      fetchGroups();
      confirmModal = false;
      callout.set({
        msg: $_("group.delete.callout"),
        status: "success",
        timeout: true,
      });
    }
  }

  function handleSearch() {
    searched = groups
      .filter(
        (group) => group.roles?.includes(search.role) || search.role === "all",
      )
      .filter((group) =>
        group.name.toUpperCase().includes(search.name.trim().toUpperCase()),
      );
    if (order.by) {
      sort(order.by, false);
    } else {
      setUrl({
        ...search,
        orderBy: order.by,
        orderAsc: order.asc,
        groupModal: modalType,
        groupId: selectedGroup?.id,
      });
    }
  }

  function handleReset() {
    search = {
      name: "",
      role: "all",
    };
    order = { asc: true };
    handleSearch();
  }

  function openModal(type, group = null) {
    modal = true;
    modalType = type;
    selectedGroup = group;
    setModalParams("groupModal", "groupId", { type, id: group?.id });
  }

  function closeModal() {
    modal = false;
    modalType = null;
    selectedGroup = null;
    clearModalParams("groupModal", "groupId");
  }

  onMount(async () => {
    await fetchRoles();
    await fetchGroups();
    fetchContract();

    const query = parseUrl(window.location.search);

    search = { ...search, name: query.name || "", role: query.role || "all" };

    if (query.orderBy) {
      order.by = query.orderBy;
      order.asc = query.orderAsc === "true";
    }

    if (query.groupModal) {
      if (query.groupId)
        selectedGroup = groups.find((g) => g.id === query.groupId);
      openModal(query.groupModal, selectedGroup);
    }

    handleSearch();
  });
</script>

<svelte:head>
  <title>
    {$_("common.groups")} | {$_("access-manager")}
  </title>
</svelte:head>

{#if confirmModal}
  <ConfirmModal
    bind:active={confirmModal}
    on:close={() => {
      confirmModal = false;
      selectedGroup = null;
    }}
    title={$_("delete.resource", {
      values: { resourceType: $_("common.group") },
    })}
    handleYes={() => onDeleteGroup(selectedGroup)}
    confirmationValue={selectedGroup.id}
  >
    <p>
      {@html $_("delete.warning.access1", {
        values: {
          resourceName: `${selectedGroup.name}`,
        },
      })}
    </p>
    <p class="mt-4 mb-2">
      {@html $_("delete.confirmation", {
        values: {
          resourceType: $_("common.group").toLowerCase(),
          resourceId: selectedGroup.id,
        },
      })}
    </p>
  </ConfirmModal>
{/if}

{#if modal}
  <GroupModal
    bind:active={modal}
    on:close={closeModal}
    on:success={() => {
      callout.set({
        msg: selectedGroup ? $_("group.edit.callout") : $_("group.new.callout"),
        status: "success",
        timeout: true,
      });
      fetchGroups();
      closeModal();
    }}
    data={selectedGroup}
    {modalType}
    {roles}
  />
{/if}

{#if duplicateModal}
  <DuplicateGroupModal
    bind:active={duplicateModal}
    on:close={() => {
      duplicateModal = false;
      selectedGroup = null;
    }}
    on:success={() => {
      fetchGroups();
      callout.set({
        msg: $_("group.new.callout"),
        status: "success",
        timeout: true,
      });
    }}
    modalType={ModalType.Group}
    data={selectedGroup}
  />
{/if}

<Layout selected="groups">
  <Flex>
    <FlexItem>
      <h3 class="level-3 gl-title">{$_("common.groups")}</h3>
    </FlexItem>
    {#if $isAuthorized("group_create")}
      <FlexItem pushEnd>
        <Button
          on:click={() => openModal(ModalType.Post)}
          disabled={!contract ||
            (contract?.data?.groupQuota &&
              groups.length >= contract.data.groupQuota)}
        >
          <Icon icon={faPlus} />
          <Text>{$_("groups.add_group")}</Text>
        </Button>
      </FlexItem>
    {/if}
  </Flex>

  <p class="mb-5">
    {$_("groups.total", {
      values: { total: groups.length },
    })}
  </p>

  <Flex class="mb-5">
    <FlexItem class="mr-4">
      <Select bind:value={search.role} id="role">
        <SelectItem
          label={`${$_("common.roles")}: ${$_("common.all")}`}
          value="all"
        />

        {#each roles as role}
          <SelectItem
            label={`${$_("common.roles")}: ${role.name}`}
            value={role.id}
          />
        {/each}
      </Select>
    </FlexItem>

    <FlexItem style="width:300px" class="mr-4">
      <TextField
        variant="Search"
        bind:value={search.name}
        on:keydown={(e) => {
          if (e.key === "Enter") {
            handleSearch();
          }
        }}
      />
    </FlexItem>

    <FlexItem class="mr-4">
      <Button on:click={handleSearch}>
        {$_("common.search")}
      </Button>
    </FlexItem>

    <FlexItem>
      <Button outlined on:click={handleReset}>
        {$_("common.reset")}
      </Button>
    </FlexItem>
  </Flex>

  <div class="results p-2 mb-4">
    <FlexItem>
      <p role="status">
        <b>{searched ? searched.length : groups.length}</b>
        {$_("common.results")}
      </p>
    </FlexItem>

    {#if loading.contract === false && contract?.data?.groupQuota}
      <FlexItem pushEnd>
        <Flex class="align-items-center">
          <FlexItem class="mr-2">
            <b>{$_("quota.group")}:</b>
          </FlexItem>
          <FlexItem class="mr-2">
            <QuotaProgress
              amount={groups.length}
              quota={contract.data.groupQuota}
            />
          </FlexItem>
          {#if groups.length >= contract.data.groupQuota}
            <FlexItem>
              <InfoBox
                title={$_("quota.group.infobox.title")}
                tooltipLeft="null"
                tooltipRight="20%">{$_("quota.group.infobox.text")}</InfoBox
              >
            </FlexItem>
          {/if}
        </Flex>
      </FlexItem>
    {/if}
  </div>

  <div class="table-container">
    <SimpleDataTable
      let:index
      let:row
      data={searched || groups}
      fullWidth
      selectable={false}
      cellsCount="4"
      loading={loading.groups}
      key="id"
      valign="middle"
      variant="separate"
      hasExpansions={$isAuthorized("group_write") ||
        $isAuthorized("group_delete")}
      stickyHead
    >
      <slot name="empty" slot="empty">
        <div class="gl-message" style="text-align: center">
          {$_("common.no.display")}
        </div>
      </slot>
      <DataCell
        {index}
        id={`${row.id}-name`}
        header={$_("common.name")}
        headerIcon={order.by === "name" && order.asc
          ? faSortUp
          : order.by === "name" && !order.asc
            ? faSortDown
            : faSort}
        clickableHeader
        activeHeader
        on:clickHeader={() => sort("name")}
      >
        {#if $isAuthorized("user_read")}
          <Link
            to={`access-manager/contracts/${$contractStore.id}/groups/${row.id}/members`}
          >
            <b> {row.name}</b>
          </Link>
        {:else}
          <b> {row.name}</b>
        {/if}
        <p class="text-light description" title={row.description}>
          {row.description ?? ""}
        </p>
      </DataCell>

      <DataCell {index} id={`${row.userId}-roles`} header={$_("common.roles")}>
        {#if row.roles}
          {#each row.roles.split(",") as role}
            <p>{role}</p>
          {/each}
        {:else}
          <p>-</p>
        {/if}
      </DataCell>

      <DataCell
        {index}
        id={`${row.id}-modified`}
        header={$_("common.last.modified")}
        headerIcon={order.by === "modified" && order.asc
          ? faSortUp
          : order.by === "modified" && !order.asc
            ? faSortDown
            : faSort}
        clickableHeader
        activeHeader
        on:clickHeader={() => sort("modified")}
      >
        {row.metadata?.modifiedAt
          ? format(new Date(row.metadata.modifiedAt), "dd.MM.yyyy HH:mm:ss")
          : "-"}
        {#if row.metadata?.modifiedBy}
          <p class="text-light" style="font-size:14px">
            {$_("common.by")}
            {row.metadata.modifiedBy}
          </p>
        {/if}
      </DataCell>

      <DataTableExpansion slot="expansion" let:row>
        {#if row.isExpanded}
          <DataTableExpansionContent colspan={7}>
            <Flex class="justify-content-end">
              {#if $isAuthorized("group_create")}
                <FlexItem>
                  <Button
                    class="mb-2"
                    size="tiny"
                    disabled={!contract ||
                      (contract?.data?.groupQuota &&
                        groups.length >= contract.data.groupQuota)}
                    on:click={() => {
                      duplicateModal = true;
                      selectedGroup = row;
                    }}
                  >
                    <Icon icon={faClone} />
                    <Text>{$_("common.duplicate")}</Text>
                  </Button>
                </FlexItem>
                <FlexItem>
                  <div class="vertical-line mx-3" />
                </FlexItem>
              {/if}
              <FlexItem>
                {#if $isAuthorized("group_write")}
                  <Button
                    outlined
                    class="mr-2  mb-2"
                    size="tiny"
                    on:click={() => openModal(ModalType.Put, row)}
                  >
                    <Icon icon={faPen} />
                    <Text>{$_("buttons.data")}</Text>
                  </Button>
                {/if}

                {#if $isAuthorized("group_write") && $isAuthorized("role_read")}
                  <Button
                    outlined
                    class="mr-2 mb-2"
                    size="tiny"
                    on:click={() => {
                      navigate(`groups/${row.id}/roles`);
                    }}
                  >
                    <Icon icon={faPen} />
                    <Text>{$_("common.roles")}</Text>
                  </Button>
                {/if}

                {#if $isAuthorized("group_write") && $isAuthorized("user_read")}
                  <Button
                    outlined
                    class="mr-2  mb-2"
                    size="tiny"
                    on:click={() => {
                      navigate(`groups/${row.id}/members`);
                    }}
                  >
                    <Icon icon={faPen} />
                    <Text>{$_("common.members")}</Text>
                  </Button>
                {/if}
                {#if $isAuthorized("group_delete")}
                  <Button
                    outlined
                    size="tiny"
                    status="error"
                    on:click={() => {
                      selectedGroup = row;
                      confirmModal = true;
                    }}
                  >
                    <Icon icon={faTrashAlt} />
                    <Text>{$_("buttons.delete")}</Text>
                  </Button>
                {/if}
              </FlexItem>
            </Flex>
          </DataTableExpansionContent>
        {/if}
      </DataTableExpansion>
    </SimpleDataTable>
  </div>
</Layout>

<style>
  .results {
    background-color: var(--gl-background);
    border-radius: 8px;
    display: flex;
  }
</style>
